'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from 'react';
import styled from '@emotion/styled';
import { formatTweetDate } from '../../utils';
import { Avatar } from '@edusense/shared-ui-kit';
import { ConfirmModal } from '@edusense/shared-ui-components';
// Lucide React 아이콘 import
import { MessageCircle as MessageCircleIcon } from 'lucide-react';
import { RepeatIcon } from 'lucide-react';
import { HeartIcon } from 'lucide-react';
import { Trash2 as Trash2Icon } from 'lucide-react';
const CardContainer = styled.div`
  padding: ${(props)=>props.compact ? '12px' : '16px'};
  border-bottom: 1px solid var(--theme-border, #e1e8ed);
  background: var(--theme-surface, #ffffff);
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  position: relative;

  &:hover {
    background: var(--theme-surface-hover, #f7f9fa);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  
  &:last-child {
    border-bottom: none;
  }
`;
const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
`;
const AvatarWrapper = styled.div`
  margin-right: 12px;
`;
const UserInfo = styled.div`
  flex: 1;
`;
const DisplayName = styled.span`
  font-weight: 600;
  color: var(--theme-text-primary, #14171a);
  margin-right: 4px;
`;
const Username = styled.span`
  color: var(--theme-text-secondary, #657786);
  margin-right: 4px;
`;
const Timestamp = styled.span`
  color: var(--theme-text-secondary, #657786);
`;
const Content = styled.div`
  font-size: 15px;
  line-height: 1.5;
  color: var(--theme-text-primary, #14171a);
  margin-bottom: 12px;
  white-space: pre-wrap;
  
  .mention {
    color: #1da1f2;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  .hashtag {
    color: #1da1f2;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  .link {
    color: #1da1f2;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;
const Actions = styled.div`
  display: flex;
  justify-content: space-between;
  max-width: 425px;
`;
const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  border: none;
  background: none;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${(props)=>{
    if (props.active && props.color) return props.color;
    if (props.active) return '#1da1f2';
    return 'var(--theme-text-secondary, #657786)';
}};
  
  &:hover {
    background: ${(props)=>{
    if (props.active && props.color) return `${props.color}20`;
    if (props.active) return 'rgba(29, 161, 242, 0.1)';
    return 'var(--theme-surface-hover, #f7f9fa)';
}};
    color: ${(props)=>{
    if (props.color) return props.color;
    return '#1da1f2';
}};
  }

  svg {
    width: 16px;
    height: 16px;
  }

  span {
    font-size: 13px;
    font-weight: 500;
  }
`;
// 트윗 내용을 파싱해서 멘션, 해시태그, 링크를 하이라이팅하는 함수
const parseContent = (content)=>{
    const parts = content.split(/(\s+|@\w+|#\w+|https?:\/\/[^\s]+)/g);
    return parts.map((part, index)=>{
        if (part.startsWith('@')) {
            return /*#__PURE__*/ _jsx("span", {
                className: "mention",
                children: part
            }, index);
        } else if (part.startsWith('#')) {
            return /*#__PURE__*/ _jsx("span", {
                className: "hashtag",
                children: part
            }, index);
        } else if (part.startsWith('http')) {
            return /*#__PURE__*/ _jsx("a", {
                href: part,
                target: "_blank",
                rel: "noopener noreferrer",
                className: "link",
                children: part
            }, index);
        }
        return part;
    });
};
export function TweetCard({ tweet, onLike, onRetweet, onReply, onDelete, showActions = true, compact = false }) {
    const handleLike = (e)=>{
        e.stopPropagation();
        onLike?.(tweet.id);
    };
    const handleRetweet = (e)=>{
        e.stopPropagation();
        onRetweet?.(tweet.id);
    };
    const handleReply = (e)=>{
        e.stopPropagation();
        onReply?.(tweet);
    };
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const handleDelete = (e)=>{
        e.stopPropagation();
        setIsDeleteModalOpen(true);
    };
    const handleConfirmDelete = ()=>{
        onDelete?.(tweet.id);
        setIsDeleteModalOpen(false);
    };
    const handleCancelDelete = ()=>{
        setIsDeleteModalOpen(false);
    };
    return /*#__PURE__*/ _jsxs(CardContainer, {
        compact: compact,
        children: [
            /*#__PURE__*/ _jsxs(Header, {
                children: [
                    /*#__PURE__*/ _jsx(AvatarWrapper, {
                        children: /*#__PURE__*/ _jsx(Avatar, {
                            profileImage: tweet.author.avatar,
                            username: tweet.author.username,
                            name: tweet.author.displayName,
                            size: "md",
                            alt: tweet.author.displayName
                        })
                    }),
                    /*#__PURE__*/ _jsxs(UserInfo, {
                        children: [
                            /*#__PURE__*/ _jsx(DisplayName, {
                                children: tweet.author.displayName
                            }),
                            /*#__PURE__*/ _jsxs(Username, {
                                children: [
                                    "@",
                                    tweet.author.username
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(Timestamp, {
                                children: [
                                    "· ",
                                    formatTweetDate(tweet.createdAt)
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Content, {
                children: parseContent(tweet.content)
            }),
            showActions && /*#__PURE__*/ _jsxs(Actions, {
                children: [
                    /*#__PURE__*/ _jsxs(ActionButton, {
                        onClick: handleReply,
                        color: "#1da1f2",
                        children: [
                            /*#__PURE__*/ _jsx(MessageCircleIcon, {}),
                            tweet.replies > 0 && /*#__PURE__*/ _jsx("span", {
                                children: tweet.replies
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(ActionButton, {
                        active: tweet.isRetweeted,
                        onClick: handleRetweet,
                        color: "#00ba7c",
                        children: [
                            /*#__PURE__*/ _jsx(RepeatIcon, {}),
                            tweet.retweets > 0 && /*#__PURE__*/ _jsx("span", {
                                children: tweet.retweets
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(ActionButton, {
                        active: tweet.isLiked,
                        onClick: handleLike,
                        color: "#f91880",
                        children: [
                            /*#__PURE__*/ _jsx(HeartIcon, {
                                fill: tweet.isLiked ? '#f91880' : 'none'
                            }),
                            tweet.likes > 0 && /*#__PURE__*/ _jsx("span", {
                                children: tweet.likes
                            })
                        ]
                    }),
                    onDelete && /*#__PURE__*/ _jsx(ActionButton, {
                        onClick: handleDelete,
                        color: "#e74c3c",
                        children: /*#__PURE__*/ _jsx(Trash2Icon, {})
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(ConfirmModal, {
                isOpen: isDeleteModalOpen,
                title: "트윗 삭제",
                message: "이 트윗을 삭제하시겠습니까?",
                confirmText: "삭제",
                cancelText: "취소",
                isDangerous: true,
                onConfirm: handleConfirmDelete,
                onCancel: handleCancelDelete
            })
        ]
    });
}
